// reporting (catalog-outline.md "reporting"; metrics-reporting plan §4.4–4.7): the metric layer and the named reports as
// queries clients call, and exports as jobs. Every figure is an integer in its unit (minor units, seconds, counts,
// thousandths) on the wire as a decimal string; a client formats it, never recomputes it. A query the interactive path
// should not run (a detail range over 366 days, or one that hit its timeout) answers `requires_export`, and the client
// offers "Prepare download" (`reports.requestExport`).
import { z } from 'zod';
import type { PermissionKey } from '../../permissions/catalog.ts';
import { defineCommand, defineQuery } from '../../define/operations.ts';
import { defineError } from '../../define/errors.ts';
import { Integer, LocalDate, LocationId } from '../../primitives/codecs.ts';
import { timeSegmentCommands, timeSegmentQueries } from './time-segments.ts';

export * from './time-segments.ts';

/** Business dates (inclusive), optional weekdays and wall-clock windows (minutes, may wrap past midnight). */
export const ReportWindow = z
  .object({
    locationIds: z.array(LocationId).min(1).max(50),
    from: LocalDate,
    to: LocalDate,
    weekdays: z.object({ days: z.array(z.int().min(1).max(7)).min(1).max(7), basis: z.enum(['business', 'calendar']) }).optional(),
    timeOfDay: z.array(z.object({ start: z.int().min(0).max(1439), end: z.int().min(0).max(1440) })).min(1).max(8).optional(),
    /** One drawer's orders and refunds only (the Drawer detail's tabs and Breakdown statement); absent = every till. */
    tillSessionId: z.uuid().optional(),
    /** Only these drawers' orders and refunds (the End of Day report's closed drawers, legacy summary3); empty = none. */
    tillSessionIds: z.array(z.uuid()).max(200).optional(),
    /**
     * The order states the sales figures count (the Dashboard's Order Status: All = closed and open, Completed =
     * closed, Open = open); absent = closed orders, as every report.
     */
    orderStates: z.array(z.enum(['closed', 'open'])).min(1).max(2).optional(),
    /**
     * Which business date a refund counts on (C-02 / MR-D2, L736): absent, metrics REFUND_DATE's (the day it was given,
     * Andrew 2026-10-19); `refund`, the day given; `order`, the refunded order's, as legacy files it (B574). Only Radish
     * staff and the legacy parity claims may name it, never a merchant or an assistant: the server refuses it from
     * anyone else (`forbidden`, permission `refundsOn`). `order` is answered from the fact rows, so its range is bounded
     * like any detail read.
     */
    refundsOn: z.enum(['refund', 'order']).optional(),
  })
  .meta({ id: 'ReportWindow' });

const MemberDim = z.enum(['employee', 'product', 'tender_method', 'job_role', 'category', 'department', 'dining_option']);
/** B1708: `line_name` groups line figures by the name each line was sold under (the Employee Sales breakdown's no-product row). */
const GroupDim = z.enum(['business_date', 'month', 'business_weekday', 'q15', 'hour', ...MemberDim.options, 'line_name']);

/** Member filters: only these members' rows (ids; the none member is all zeros: Misc Category, Other, No Dining Option). */
const MemberFilters = z.partialRecord(MemberDim, z.array(z.string().max(64)).max(200));

/**
 * The Item Sales filters (legacy's Filter by device, Category, Department, Employee). A device keeps the orders rung on
 * its tills and those orders' refunds; an order with no till link (an imported legacy order) is on no device.
 */
const ItemFilters = z.object({
  device: z.array(z.uuid()).max(200).optional(),
  category: z.array(z.string().max(64)).max(200).optional(),
  department: z.array(z.string().max(64)).max(200).optional(),
  employee: z.array(z.string().max(64)).max(200).optional(),
});

export const MetricResult = z
  .object({
    rows: z.array(z.object({ group: z.record(z.string(), z.string()), values: z.record(z.string(), Integer) })),
    totals: z.record(z.string(), Integer),
  })
  .meta({ id: 'MetricResult' });

/** A report's answer: the figures (with how fresh the rollups are), "prepare a download instead", or "busy, retry". */
export const ReportAnswer = z
  .discriminatedUnion('kind', [
    z.object({ kind: z.literal('ok'), result: MetricResult, freshness: z.string().nullable() }),
    z.object({ kind: z.literal('requires_export'), reason: z.enum(['detail_over_366_days', 'statement_timeout']) }),
    z.object({ kind: z.literal('busy') }),
  ])
  .meta({ id: 'ReportAnswer' });

export const ReportRefused = defineError({
  code: 'reports.refused', httpStatus: 422, message: 'errors.reports.refused', retryable: false,
  details: z.object({ reason: z.string() }),
});
export const ExportNotFound = defineError({
  code: 'reports.export_not_found', httpStatus: 404, message: 'errors.reports.export_not_found', retryable: false,
  details: z.object({ exportId: z.uuid() }),
});

const reportQuery = {
  permission: 'reports.view', availability: 'location', scope: 'location', errors: [ReportRefused],
  agent: { expose: true, surfaces: ['backoffice.assistant', 'crew.manager'] },
} as const;

/** Any metric, grouped and filtered (the metric layer, plan §4.5): the AI and custom views use this. */
export const QueryMetrics = defineQuery({
  ...reportQuery,
  name: 'metrics.query',
  summary: 'Compute catalog metrics over a report window, grouped by time or member dimensions.',
  input: z.object({
    metrics: z.array(z.string().min(1).max(64)).min(1).max(40),
    groupBy: z.array(GroupDim).max(3).optional(),
    window: ReportWindow,
    filters: MemberFilters.optional(),
  }),
  output: ReportAnswer,
});

type Section = readonly PermissionKey[];
/** E-09: a back-office session also needs the report's section (legacy checked it in the browser only). */
const SALES: Section = ['reports.sales.view'];
const AUDITING: Section = ['reports.auditing.view'];
const PAYROLL: Section = ['reports.payroll.view'];
/** Sales Overview stays on View Reports alone: the dashboard (View Dashboard is locked on) reads it too. */
const DASHBOARD: Section = [];

const named = <const N extends `reports.${string}`>(name: N, summary: string, legacy: readonly string[], backofficeAlso: Section) =>
  defineQuery({ ...reportQuery, name, summary, input: z.object({ window: ReportWindow }), output: ReportAnswer, legacy, backofficeAlso });

/** A named report that takes the item filters (legacy Item Sales: Category, Department, Employee; R04). */
const filtered = <const N extends `reports.${string}`>(name: N, summary: string, legacy: readonly string[], backofficeAlso: Section) =>
  defineQuery({
    ...reportQuery, name, summary, legacy, output: ReportAnswer, backofficeAlso,
    input: z.object({ window: ReportWindow, filters: ItemFilters.optional() }),
  });

export const SalesOverviewReport = named('reports.salesOverview', 'Sales Overview (and the register X report): revenue, discounts, receipts and voids.', ['GET /v1/reports/sales-overview'], DASHBOARD);
export const SalesByDayReport = named('reports.salesByDay', 'Sales by business date.', ['GET /v1/reports/daily-summary'], SALES);
export const ItemSalesReport = filtered('reports.itemSales', 'Item Sales: quantity, gross, discounts, cash discount, net, tax and subtotal per product.', ['GET /v1/reports/item-sales'], SALES);
/** Item Sales' variant rows (legacy processItemVariations): the same figures per product and variant (group `variant`, '' = none). */
export const ItemSalesVariantsReport = filtered('reports.itemSalesVariants', "Item Sales by variant: each product's figures per variant it was sold at.", ['GET /v1/reports/item-sales'], SALES);
export const CategorySalesReport = filtered('reports.categorySales', 'Category Sales: Item Sales summed by category (none = Misc Category).', ['GET /v1/reports/category-sales'], SALES);
export const DepartmentSalesReport = filtered('reports.departmentSales', 'Department Sales: Item Sales summed by department (none = Other).', ['GET /v1/reports/department-sales'], SALES);
/**
 * Dining Option Sales (legacy GET /v1/reports/dining-options, DinningOptionsService): `filters.dining_option` keeps only
 * those options' rows in the rows and totals. Legacy lists the location's live options only (an order with no dining
 * option, or a deleted one, is in no row), so its pages pass them.
 */
export const DiningOptionSalesReport = defineQuery({
  ...reportQuery, name: 'reports.diningOptionSales', summary: 'Dining Option Sales: sales, refunds, discounts, tax, tips and gratuity per dining option, as legacy computes them.',
  legacy: ['GET /v1/reports/dining-options'], output: ReportAnswer, backofficeAlso: SALES,
  input: z.object({ window: ReportWindow, filters: z.object({ dining_option: z.array(z.string().max(64)).max(200).optional() }).optional() }),
});
/**
 * Time Segment Sales (legacy GET /v1/reports/time-segments): one row per time segment (`group.time_segment` = its id),
 * each order under the first segment, by creation, whose minutes hold its open time; an order outside every segment is
 * in no row, as legacy. Figures as legacy's report: Gross Sales its subtotal, Discounts what it charged.
 */
export const TimeSegmentSalesReport = named('reports.timeSegmentSales', 'Time Segment Sales: gross, discounts, net, tax, tips, gratuity and total per time segment (daypart).', ['GET /v1/reports/time-segments'], SALES);

/**
 * A report's chart: net and gross per business date for each member of one dimension (the Item, Category, Department,
 * Employee and Dining Option charts; the page draws the top five).
 */
export const SalesTrendReport = defineQuery({
  ...reportQuery,
  name: 'reports.salesTrend', backofficeAlso: SALES,
  summary: 'Gross and net sales per business date for each product, category, department, employee or dining option (report charts).',
  input: z.object({
    window: ReportWindow, dimension: z.enum(['product', 'category', 'department', 'employee', 'dining_option']),
    filters: ItemFilters.optional(),
  }),
  output: ReportAnswer,
});
export const EmployeeSalesReport = named('reports.employeeSales', 'Employee Sales: sales, tips and gratuity per employee.', ['GET /v1/reports/employee-sales'], SALES);
export const PaymentsReport = named('reports.payments', 'Summary of receipts by payment method.', ['GET /v1/reports/payments'], SALES);
/**
 * B1777: the Payment Summary's custom and delivery-app tender rows (legacy summary2 / summary3 `tender_types`): the amount
 * and tip of the window's closed orders' tenders by `group.tender_type` (`id:<tender type>` a live custom tender,
 * `name:<legacy name>` an imported one, `source:<delivery app>` a delivery-app card, '' none). The pages name and seed the
 * rows from `reports.tenderTypes` (metrics `tenderTypeRows`).
 */
export const PaymentsByTenderTypeReport = named('reports.paymentsByTenderType', "Payments by tender type: what each custom tender type and delivery app took on the window's closed orders.", ['GET /v1/reports/summary2', 'GET /v1/reports/summary3'], DASHBOARD);
/**
 * The Dashboard's Top Items Sales (legacy GET /v1/reports/dashboard `top_item_sales_qty`): quantity (`items_sold`) and
 * gross per line name as sold (`group.line_name`: a misc line's typed name, a product's name at the time), the counted
 * order states as the window's `orderStates`. The page picks the top five.
 */
/** The names products were sold under (group `product` and `line_name`): Item Sales names a deleted product by them, as legacy keeps its name. */
export const ItemNamesReport = named('reports.itemNames', 'Item names: each product sold in the window with the name it was sold under.', ['GET /v1/reports/item-sales'], SALES);
export const DashboardTopItemsReport = named('reports.dashboardTopItems', 'Dashboard Top Items: quantity and gross sales per item name as sold (misc items by their own names).', ['GET /v1/reports/dashboard'], DASHBOARD);
export const PayrollReport = named('reports.payroll', 'Payroll Summary: regular, overtime and double-time hours and pay per employee.', ['GET /v1/reports/payroll'], PAYROLL);

/** One till session of a window (R14 Drawers/Banks list; the EOD's cash section), every figure core's cash figures. */
const TillSessionFigures = z.object({
  tillSessionId: z.uuid(), businessDate: LocalDate, deviceId: z.string(), ownerEmployeeId: z.string(), letter: z.string(),
  tillState: z.enum(['open', 'closed', 'auto_closed']), openedAt: z.string(), closedAt: z.string().nullable(), closedByEmployeeId: z.string().nullable(),
  openingFloat: Integer, cashSales: Integer, movementsIn: Integer, movementsOut: Integer, drops: Integer, tipPayouts: Integer, cashRefunds: Integer,
  paidIns: Integer, paidOuts: Integer, nonCashTipPayouts: Integer, gratuityPayouts: Integer, settlements: Integer,
  expected: Integer, counted: Integer.nullable(), overShort: Integer.nullable(),
  /** A register drawer or a server bank, its closeout's progress, and its closed orders' count, card tips and gratuity (Drawers/Banks list). */
  kind: z.enum(['register', 'server_bank']).optional(), closeout: z.enum(['none', 'submitted', 'approved']).optional(),
  orderCount: Integer.optional(), cardTips: Integer.optional(), gratuity: Integer.optional(),
  /**
   * HUB-34 (Jarrett, local-first-offline-mode architecture.md:181): the business-day close deferred it while its device
   * had activity the server had not received, then closed it after the grace with that activity still unsynced
   * (TillSessionClosed v4 `unsynced`). Absent otherwise, and from a server that predates it.
   */
  unsynced: z.literal(true).optional(),
  /** The tip and gratuity payouts a server bank's approval paid out of it, inside `expected` (the Drawers list's B446). Absent: none. */
  settledPayouts: Integer.optional(),
  /** The tips part of `settledPayouts` (the rest is gratuity), which legacy's End of Day lists on no payout line. Absent: none. */
  settledTipPayouts: Integer.optional(),
  /**
   * A server bank's lines as the End of Day reads them (lane RP, RP4; legacy summary3 over the bank's cash management
   * rows): its own, plus the tips and gratuity its approved closeout paid out and the cash it settled out to a drawer or
   * in from one, counted at the close amount legacy stores. The Drawers/Banks pages keep the lines above. Absent on a
   * register drawer, an open bank, an imported bank (its own legacy rows already), or a row written before it was.
   */
  endOfDay: z.object({
    movementsIn: Integer, movementsOut: Integer, tipPayouts: Integer, nonCashTipPayouts: Integer, gratuityPayouts: Integer, settlements: Integer,
    expected: Integer, counted: Integer.nullable(),
    /** With a next-day bank's cash paid out (or in) by the business-day job. */
    paidIns: Integer, paidOuts: Integer,
  }).optional(),
}).meta({ id: 'TillSessionFigures' });

/** The drawers opened in a window and the window's cash deposit (MR-D18: "Not available" while any is uncounted). */
export const DrawerAnswer = z
  .discriminatedUnion('kind', [
    z.object({
      kind: z.literal('ok'),
      sessions: z.array(TillSessionFigures),
      openingFloat: Integer, cashSales: Integer, expectedCash: Integer, actualCloseoutCash: Integer.nullable(), overShort: Integer.nullable(),
      deposit: Integer.nullable(), drops: Integer,
    }),
    z.object({ kind: z.literal('busy') }),
  ])
  .meta({ id: 'DrawerAnswer' });

export const DrawersReport = defineQuery({
  ...reportQuery, agent: { expose: false },
  name: 'reports.drawers', backofficeAlso: AUDITING,
  summary: 'Drawers/Banks: the till sessions opened in a window, their cash figures, and the expected cash deposit of the window.',
  /** `tillKind`: register drawers only or server banks only (the Drawers and Server Banks tabs); absent = every till. */
  input: z.object({ window: ReportWindow, tillKind: z.enum(['register', 'server_bank']).optional() }),
  output: DrawerAnswer,
  legacy: ['GET /v1/reports/drawers'],
});

/** One settled part of a server bank's closeout: into a register drawer (its letter and device) or the safe, and who approved it. */
const BankSettlementPart = z.object({
  destination: z.enum(['cash_box', 'deposit']), drawerLetter: z.string().nullable(), deviceId: z.string().nullable(), approvedByEmployeeId: z.string().nullable(),
});

/** One drawer (Reports › Auditing › Drawers/Banks › a drawer, SCR-052): its details and Cash Summary lines. */
export const DrawerDetailReport = defineQuery({
  ...reportQuery, agent: { expose: false },
  name: 'reports.drawerDetail', backofficeAlso: AUDITING,
  summary: 'One drawer: who opened and closed it and when, its opening amount, cash collected, refunds, paid ins and outs, drops, payouts, settlements and expected closeout cash, its order, void and refund counts, the payments taken for its orders and its cash management lines.',
  input: z.object({ locationId: LocationId, tillSessionId: z.uuid() }),
  output: z.discriminatedUnion('kind', [
    z.object({
      kind: z.literal('ok'), session: TillSessionFigures,
      counts: z.object({ orders: Integer, voids: Integer, refunds: Integer }),
      payments: z.array(z.object({
        tenderId: z.uuid(), orderId: z.uuid(), orderNumber: z.string().nullable(), businessDate: LocalDate, localMinute: z.int().min(0).max(1439),
        method: z.string(), amount: Integer, tip: Integer,
        /** Legacy's tender ticket ("A-42": the drawer's letter and the payment's ticket number on it). Absent from a server that predates it. */
        ticket: z.string().max(64).optional(),
        /**
         * B1477: the card's last four digits (legacy `tenders.cc_last_four`), the Card Ending column; null when none was
         * recorded (every cash payment). Only ever the last four (PCI). Absent from a server that predates it.
         */
        cardLast4: z.string().regex(/^[0-9]{4}$/u).nullable().optional(),
        /**
         * B1850: when the payment itself was taken, on the location's clock (legacy DrawerPayments prints the tender's
         * `created`), where `businessDate` and `localMinute` are its order's. Absent from an older server, and on a payment
         * whose fact predates rollup v88 until the location is restated.
         */
        takenCalendarDate: LocalDate.optional(),
        takenLocalMinute: z.int().min(0).max(1439).optional(),
      })),
      /**
       * Cash Management (legacy DrawerCashManagement): each paid in, paid out, cash drop, tip payout and gratuity payout,
       * oldest first, with who was signed in, who a payout went to, the description typed and the amount moved (positive).
       * `tip_payout_safe_withdrawal`: an imported legacy tip payout paid from the safe (legacy lists it with a blank Type; B460).
       * Absent from a server that predates it.
       */
      cashManagement: z.array(z.object({
        movementId: z.uuid(), purpose: z.enum(['paid_in', 'paid_out', 'drop', 'tip_payout', 'gratuity_payout', 'tip_payout_safe_withdrawal']), amount: Integer,
        employeeId: z.string().nullable(), recipientEmployeeId: z.string().nullable(), note: z.string().nullable(), at: z.string(),
      })).optional(),
      /**
       * A server bank's Closeout Summary (legacy ServerBankSettlementStatus, REP-020): whether its cash was collected (or
       * paid out to the server) and its tips paid, whether both were settled together, when a manager last approved, and
       * each part's destination (a register drawer, by its letter and device, or the safe) and approving manager. Null on
       * a register drawer or a bank not yet submitted; absent from a server that predates it.
       */
      bankCloseout: z.object({
        cash: z.enum(['unpaid', 'collected', 'paid']), tips: z.enum(['unpaid', 'paid']), combined: z.boolean(), approvedAt: z.string().nullable(),
        cashPart: BankSettlementPart.nullable(), tipPart: BankSettlementPart.nullable(),
        /** Its submitted cash collected less its tips and gratuity (legacy `closure_intent.close_out`, the Breakdown's Closeout Cash). Absent from a server that predates it. */
        closeoutCash: Integer.optional(),
      }).nullable().optional(),
      /**
       * A register drawer's Settlements (Server Banks) (legacy CashDrawerSettelements.vue, REP-020): one row per record
       * legacy books on the drawer when a manager settles a server bank's closeout into it, oldest first. `kind`
       * settled_in / settled_out (cash and tips together, or the cash alone), tip_payout / gratuity_payout (the tips after
       * the cash, each when above zero); `amount` signed, money into the drawer positive; `combined` the bank settled
       * both together; the bank's cash collected, non-cash tips and gratuity for the Description; `serverEmployeeId` the
       * bank's server. Empty on a server bank; absent from a server that predates it.
       */
      bankSettlements: z.array(z.object({
        bankTillSessionId: z.uuid(), serverEmployeeId: z.string().nullable(), at: z.string(),
        kind: z.enum(['settled_in', 'settled_out', 'tip_payout', 'gratuity_payout']), amount: Integer, combined: z.boolean(),
        cashCollected: Integer, nonCashTips: Integer, gratuity: Integer,
      })).optional(),
    }),
    z.object({ kind: z.literal('not_found') }),
  ]),
  legacy: ['GET /v1/reports/drawers/{id}/breakdown'],
});

/**
 * One drawer's Summary of Receipts (lane BL, B1654: the Breakdown dialog, its PDF and XLSX): legacy summary4's
 * `payments` and `tender_types` for the drawer, over the tenders taken on it, by metrics `drawerReceipts`. `card` is
 * Card (Exp. Deposit) when `trueMargin`; `tenderTypes` lists every custom tender type of the location in legacy's order,
 * deleted ones too, each with what its tenders took on the drawer; `total` is legacy's Total Receipts.
 */
export const DrawerReceiptsReport = defineQuery({
  ...reportQuery, agent: { expose: false },
  name: 'reports.drawerReceipts', backofficeAlso: AUDITING,
  summary: "One drawer's Summary of Receipts: what its tenders took by card, cash, check, gift card, house account and each custom tender type, and the total.",
  input: z.object({ locationId: LocationId, tillSessionId: z.uuid() }),
  output: z.discriminatedUnion('kind', [
    z.object({
      kind: z.literal('ok'), trueMargin: z.boolean(),
      card: Integer, cash: Integer, checks: Integer, giftCard: Integer, houseAccount: Integer,
      tenderTypes: z.array(z.object({ name: z.string(), amount: Integer })), total: Integer,
    }),
    z.object({ kind: z.literal('not_found') }),
  ]),
  legacy: ['GET /v1/reports/summary4/{drawer_id}'],
});

const NamedAmounts = z.array(z.object({ name: z.string(), amount: Integer }));

/**
 * One drawer's DRAWER SALES SUMMARY (the drawer Breakdown's sales rows; lane BL, B1671), as legacy's summary4 prints them:
 * the Revenue Summary of the location's program, the Discount Summary, Voids, True Margin's Taxable vs. Non-taxable
 * Sales, Sales by Dining Option (every option, deleted ones too, then Total), Department Sales (then Other and Total) and Category
 * Sales (then Misc Products).
 * The drawer's orders count whatever their business date; tips and True Margin's card figures come from the tenders taken
 * on the drawer. The Summary of Receipts is `reports.drawerReceipts`. `refundsOn` (legacy's refund dating, `order`: the
 * drawer's orders' refunds wherever handed back) is for Radish staff and the parity claims only, as a report window's.
 */
export const DrawerSalesSummaryReport = defineQuery({
  ...reportQuery, agent: { expose: false },
  name: 'reports.drawerSalesSummary', backofficeAlso: AUDITING,
  summary: "One drawer's sales summary: revenue, discounts, voids, taxable split, dining options, departments and categories, as its Breakdown prints them.",
  input: z.object({ locationId: LocationId, tillSessionId: z.uuid(), refundsOn: z.enum(['refund', 'order']).optional() }),
  output: z.discriminatedUnion('kind', [
    z.object({
      kind: z.literal('ok'), trueMargin: z.boolean(),
      revenue: z.discriminatedUnion('program', [
        z.object({
          program: z.literal('traditional'),
          grossSales: Integer, refunds: Integer, discounts: Integer, netSales: Integer, taxableSales: Integer, nonTaxableSales: Integer, tax: Integer, subtotal: Integer,
          giftCardSales: Integer, tips: Integer, gratuity: Integer, houseAccount: Integer, collected: Integer,
        }),
        z.object({
          program: z.literal('true_margin'),
          grossSales: Integer, refunds: Integer, discounts: Integer, cashDiscount: Integer, netSales: Integer, customerPaidProcessingFees: Integer, trueMarginNetSales: Integer,
          tax: Integer, tipsGratuityNetOfFees: Integer, tips: Integer, gratuity: Integer, tipsGratuityProcessingFees: Integer, cardTipRefunds: Integer, giftCardSales: Integer,
          houseAccount: Integer, collected: Integer,
          /** Taxable vs. Non-taxable Sales. */
          taxable: Integer, nonTaxable: Integer, taxableTotal: Integer,
        }),
      ]),
      discounts: z.object({ automatic: Integer, preset: Integer, custom: Integer, total: Integer }),
      voids: z.object({ amount: Integer, orderCount: z.int().min(0), itemCount: z.int().min(0) }),
      /** Each dining option, then `Total`. */
      diningOptions: NamedAmounts,
      /** Each department, then `Other` and `Total`; null when the location has none. */
      departments: NamedAmounts.nullable(),
      /** Each category, then `Misc Products`, with no Total (legacy summary4 `categories`; B1671 review B1). */
      categories: NamedAmounts.optional(),
      /**
       * The Deposit Summary (Credit Cards) (legacy summary4 `deposit`, PostProcessRecordsOperation `setDepositSummary`,
       * over the cards taken on the drawer; B1710): Credit Card Payments (Orders) and (Tips), CC Processing Fees (Tips) and
       * Estimated Deposit (CC). Every line $0.00 unless the location has a percent cash discount. The Breakdown prints it
       * on Traditional only. Absent from a server that predates it.
       */
      deposit: z.object({ orders: Integer, tips: Integer, processing: Integer, estimated: Integer }).optional(),
    }),
    z.object({ kind: z.literal('not_found') }),
    z.object({ kind: z.literal('requires_export') }),
  ]),
  legacy: ['GET /v1/reports/summary4/{drawer_id}'],
});

/** A closed order's figures (drawer "All Transactions › Orders", the Employee Sales breakdown's orders, order lookup). */
const OrderRowFigures = z.object({
  orderId: z.uuid(), businessDate: LocalDate, localMinute: z.int().min(0).max(1439), employeeId: z.string(),
  subtotal: Integer, discounts: Integer, cashDiscount: Integer, gratuity: Integer, tax: Integer, tips: Integer, total: Integer,
  /** The Employee Sales breakdown's Discounts and Total (legacy OrderResource): what the order charged, and its total then. */
  storedDiscounts: Integer, storedTotal: Integer,
  /** The Discounts a drawer's Orders tab lists in legacy (retaken off the subtotal; B4 / L117). Absent from a server that predates it. */
  listedDiscounts: Integer.optional(),
  /**
   * The Employee Sales breakdown's Cash Discount (True Margin; legacy CalculateTenderCashDiscount over the order's non-card
   * tenders, voided ones included, B644). Absent from a server that predates it.
   */
  breakdownCashDiscount: Integer.optional(),
  /**
   * The Employee Sales breakdown's Subtotal (legacy OrderResource `orders.subtotal`): every line at its stored subtotal,
   * gift-card loads included (B1686), where `subtotal` leaves them out. Absent from a server that predates it.
   */
  storedSubtotal: Integer.optional(),
  /**
   * B1848: the calendar date of the order's open time on the location's clock (a drawer's Orders Date & Time), where
   * `businessDate` is its business day. Absent from an older server, and on an order whose fact predates rollup v88 until
   * the location is restated.
   */
  calendarDate: LocalDate.optional(),
  /**
   * The Cash Discount a drawer's Orders tab lists in legacy (DrawerBreakdownOrderResource: the fees of the order's standing
   * non-card tenders, a gift card load's share too; B1733). Absent from a server that predates it.
   */
  listedCashDiscount: Integer.optional(),
  /** The order's number on the till ("Order" in the drawer's Orders tab); null for an order projected before it was stored. */
  orderNumber: z.string().nullable(),
  /** Still open (asked with `includeOpen`): the drawer's Orders tab offers Force Complete on it (DLG-073). False when absent. */
  open: z.boolean().default(false),
}).meta({ id: 'OrderRowFigures' });

export const OrderListReport = defineQuery({
  ...reportQuery, agent: { expose: false },
  name: 'reports.orderList',
  // One employee's orders are the Employee Sales breakdown (a Sales endpoint in legacy); without one, the drawer and
  // order lookup audit list (Auditing). Coordinator decision 2026-10-12.
  backofficeAlso: { whenInput: 'employeeId', given: SALES, otherwise: AUDITING },
  summary: 'The closed orders of a window (bounded detail, at most 366 days), optionally one employee’s.',
  input: z.object({
    window: ReportWindow, employeeId: z.uuid().optional(),
    /** With unfinished orders too (a drawer's Orders tab: legacy lists every order on the drawer but a voided one). Absent: closed only. */
    includeOpen: z.boolean().optional(),
  }),
  output: z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('ok'), orders: z.array(OrderRowFigures) }),
    z.object({ kind: z.literal('requires_export'), reason: z.enum(['detail_over_366_days', 'statement_timeout']) }),
    z.object({ kind: z.literal('busy') }),
  ]),
  legacy: ['GET /v1/reports/v2/employee-sales/{employee_id}', 'POST /v1/orders/lookup'],
});

const AdjustmentPlace = {
  orderId: z.uuid(), orderNumber: z.string().nullable(), businessDate: LocalDate, calendarDate: LocalDate, localMinute: z.int().min(0).max(1439),
  employeeId: z.string(), reason: z.string().nullable(),
};

/** A Voids row: a voided order (Full Order) or an order with voided payments (Payment Only). */
const VoidRow = z.object({
  ...AdjustmentPlace, voidExtent: z.enum(['full_order', 'payment_only']), paymentVoided: z.boolean(), voidPayment: Integer, voidOrder: Integer,
  /** Its item lines (legacy `items_count`: the order's items with a quantity above zero), as a drawer's Voids tab counts them. Absent from an older server. */
  itemsCount: Integer.optional(),
}).meta({ id: 'AdjustmentVoidRow' });

/** A Deletions row: one void of a line (`voidKey` tells an order's voids apart), with the subtotal and tax it took off the order. */
const DeletionRow = z.object({ ...AdjustmentPlace, voidKey: z.string(), itemName: z.string(), qtyMilli: Integer, itemAmount: Integer }).meta({ id: 'AdjustmentDeletionRow' });

export const AdjustmentsReport = defineQuery({
  ...reportQuery, agent: { expose: false },
  name: 'reports.adjustments', backofficeAlso: AUDITING,
  summary: 'Adjustments (Voids and Deletions): voided orders, voided payments and line voids of a window (bounded detail, at most 366 days).',
  input: z.object({ window: ReportWindow }),
  output: z.discriminatedUnion('kind', [
    z.object({
      kind: z.literal('ok'), voids: z.array(VoidRow), deletions: z.array(DeletionRow),
      /** The Total rows: Σ Void Payment Amt and Void Order Amt over the voids, Σ Item Amount over the deletions. */
      totals: z.object({ voidPayment: Integer, voidOrder: Integer, itemAmount: Integer }),
    }),
    z.object({ kind: z.literal('requires_export'), reason: z.enum(['detail_over_366_days', 'statement_timeout']) }),
    z.object({ kind: z.literal('busy') }),
  ]),
  legacy: ['GET /v1/reports/adjustments', 'GET /v1/reports/voids'],
});

/** A Discounts row: one discount name, how many times it was applied and what it took off in all. */
const DiscountRow = z.object({ name: z.string(), applied: Integer, amount: Integer }).meta({ id: 'DiscountReportRow' });

export const DiscountsReport = defineQuery({
  ...reportQuery, agent: { expose: false },
  name: 'reports.discounts', backofficeAlso: SALES,
  summary: 'Discounts: each discount and comp of the closed orders opened in a window, by name, with how often it was applied and what it took off.',
  input: z.object({ window: ReportWindow }),
  output: z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('ok'), rows: z.array(DiscountRow), totals: z.object({ applied: Integer, amount: Integer }) }),
    z.object({ kind: z.literal('requires_export'), reason: z.enum(['detail_over_366_days', 'statement_timeout']) }),
    z.object({ kind: z.literal('busy') }),
  ]),
  legacy: ['GET /v1/reports/discounts'],
});

/** A Refunds row: one refund on its own business date, with what its payments gave back. */
const RefundRow = z.object({
  refundId: z.uuid(), orderId: z.uuid(), orderNumber: z.string().nullable(), businessDate: LocalDate, calendarDate: LocalDate, localMinute: z.int().min(0).max(1439),
  employeeId: z.string(), comments: z.string().nullable(), itemsMilli: Integer, orderRefund: Integer, tipRefund: Integer, total: Integer,
}).meta({ id: 'RefundReportRow' });

export const RefundsReport = defineQuery({
  ...reportQuery, agent: { expose: false },
  name: 'reports.refunds', backofficeAlso: AUDITING,
  summary: 'Refunds: every refund of a window on its own business date, with the refunded items, order refund, tip refund and total (bounded detail, at most 366 days).',
  input: z.object({ window: ReportWindow }),
  output: z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('ok'), refunds: z.array(RefundRow), totals: z.object({ orderRefund: Integer, tipRefund: Integer, total: Integer }) }),
    z.object({ kind: z.literal('requires_export'), reason: z.enum(['detail_over_366_days', 'statement_timeout']) }),
    z.object({ kind: z.literal('busy') }),
  ]),
  legacy: ['GET /v1/reports/refunds'],
});

/** The Refund Details slide-over of one refund (legacy "Order #623": Refund Details, Refund Summary, Refunded Payments). */
const RefundDetail = z.object({
  refundId: z.uuid(), orderId: z.uuid(), orderNumber: z.string().nullable(), calendarDate: LocalDate, localMinute: z.int().min(0).max(1439), employeeId: z.string(),
  tabName: z.string().nullable(), diningOptionId: z.string().nullable(),
  lines: z.array(z.object({ name: z.string(), qtyMilli: Integer, net: Integer })),
  itemsNet: Integer, tax: Integer, amount: Integer,
  /** "Cash Discount Adj." (shown with a plus) and whether it shows; whether Subtotal Item Refunds and Tax Refund show. */
  cashDiscountAdj: Integer, cashDiscountAdjShown: z.boolean(), splitShown: z.boolean(),
  /** The "Custom Amount Refund" line's amount, or null when it does not show (legacy never shows it). */
  customAmountLine: Integer.nullable(),
  payments: z.array(z.object({ ticket: z.string(), method: z.string(), amountPaid: Integer, refundAmount: Integer, refundTip: Integer })),
}).meta({ id: 'RefundDetail' });

export const RefundDetailReport = defineQuery({
  ...reportQuery, agent: { expose: false },
  name: 'reports.refundDetail', backofficeAlso: AUDITING,
  summary: 'One refund in detail: when and by whom, the refunded items with the net and tax that went back, and each payment it was given back on.',
  input: z.object({ locationId: LocationId, refundId: z.uuid() }),
  output: z.discriminatedUnion('kind', [z.object({ kind: z.literal('ok'), refund: RefundDetail }), z.object({ kind: z.literal('not_found') })]),
  legacy: ['GET /v1/reports/refunds/{refundHash}/breakdown'],
});

/**
 * The Overview's "Tip paid in (source)" lines (legacy FetchTipPaidInBySources): the tips paid in recorded in a window,
 * summed by source name (first recorded first; a deleted source's are left out), and their total, which the Overview
 * adds to Total Collected and Total Receipts.
 */
export const TipsPaidInReport = defineQuery({
  ...reportQuery, agent: { expose: false },
  name: 'reports.tipsPaidIn', backofficeAlso: DASHBOARD,
  summary: 'Tips paid in by tip source over a report window, and their total (the Overview and Sales Summary lines).',
  input: z.object({ window: ReportWindow }),
  output: z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('ok'), sources: z.array(z.object({ name: z.string(), amount: Integer })), total: Integer }),
    z.object({ kind: z.literal('busy') }),
  ]),
  legacy: ['GET /v1/reports/summary2'],
});

/** The business dates with a drawer, newest first, the current business day left out (the EOD Report list, R03/R26). */
export const EodDatesReport = defineQuery({
  ...reportQuery, agent: { expose: false },
  name: 'reports.eodDates', backofficeAlso: SALES,
  summary: 'The business dates that had a drawer, newest first, today excluded (the EOD Report list).',
  input: z.object({ locationId: LocationId, before: LocalDate }),
  output: z.object({ dates: z.array(LocalDate) }),
  legacy: ['GET /v1/reports/drawers/open-dates', 'GET /v1/drawers/previous-eod-reports'],
});

const DeletedName = z.object({
  id: z.string(), name: z.string(),
  /** Its list position when deleted (B1568: Sales by Dining Option puts a deleted option among the live ones by it). Absent from a server before it. */
  sortKey: z.string().max(64).optional(),
});

/**
 * The location's deleted categories, departments and dining options with their last names: Category and Department
 * Sales, the Dashboard and the EOD name history sold under a row the catalog has since deleted by that name, as legacy
 * reads its categories, departments and dining options `withTrashed` (not "Deleted category"). Dining options (lane D5
 * round 42, additive): the X report's, End of Day's and Overview's Sales by Dining Option name a deleted one that sold.
 */
export const DeletedNamesReport = defineQuery({
  ...reportQuery, agent: { expose: false },
  name: 'reports.deletedNames', backofficeAlso: DASHBOARD,
  summary: "The location's deleted categories, departments and dining options with their last names, so reports name history sold under them.",
  input: z.object({ locationId: LocationId }),
  output: z.object({ categories: z.array(DeletedName), departments: z.array(DeletedName), diningOptions: z.array(DeletedName) }),
  legacy: ['GET /v1/reports/category-sales', 'GET /v1/reports/department-sales'],
});

/**
 * B1777: the location's custom tender types, deleted ones too, in legacy's order (imported by legacy id, then the
 * location's own as created): the Payment Summary lists each, $0.00 when unused (legacy `tenderTypes()->withTrashed()`).
 */
export const TenderTypesReport = defineQuery({
  ...reportQuery, agent: { expose: false },
  name: 'reports.tenderTypes', backofficeAlso: DASHBOARD,
  summary: "The location's custom tender types, deleted ones included, in legacy's order, so the Payment Summary lists each.",
  input: z.object({ locationId: LocationId }),
  output: z.object({ tenderTypes: z.array(z.object({ id: z.string(), name: z.string() })) }),
  legacy: ['GET /v1/reports/summary2', 'GET /v1/reports/summary3'],
});

export const EXPORTABLE_REPORTS = [
  'sales_overview', 'sales_by_day', 'item_sales', 'category_sales', 'department_sales', 'dining_option_sales', 'employee_sales', 'payments', 'payroll', 'order_list',
] as const;

const ExportState = z.enum(['queued', 'running', 'done', 'failed']);

/** Queue a report as a CSV export (exports are jobs). The same request within 10 minutes is the export already queued. */
export const RequestExport = defineCommand({
  availability: 'location',
  origin: 'any',
  name: 'reports.requestExport',
  summary: 'Prepare a report as a CSV download (a job); the same request again returns the export already queued.',
  input: z.object({ reportName: z.enum(EXPORTABLE_REPORTS), locationId: LocationId, from: LocalDate, to: LocalDate }),
  output: z.object({ exportId: z.uuid(), state: ExportState }),
  permission: 'reports.view',
  risk: 'low',
  dryRun: false,
  agent: { expose: true, surfaces: ['backoffice.assistant'] },
  emits: [],
  errors: [ReportRefused],
  legacy: ['POST /v1/reports/export'],
});

export const GetExport = defineQuery({
  ...reportQuery,
  name: 'reports.getExport',
  summary: "An export's progress, and where its file is once done.",
  input: z.object({ exportId: z.uuid() }),
  output: z.object({
    exportId: z.uuid(), reportName: z.enum(EXPORTABLE_REPORTS), state: ExportState, rowCount: z.int().nonnegative().nullable(), objectKey: z.string().nullable(),
  }),
  errors: [ExportNotFound],
});

export const reportQueries = [
  QueryMetrics, SalesOverviewReport, SalesByDayReport, ItemSalesReport, ItemSalesVariantsReport, CategorySalesReport, DepartmentSalesReport, DiningOptionSalesReport, SalesTrendReport,
  EmployeeSalesReport, TimeSegmentSalesReport, PaymentsReport, PaymentsByTenderTypeReport, PayrollReport, DashboardTopItemsReport, ItemNamesReport, DrawersReport, EodDatesReport, DeletedNamesReport, TenderTypesReport, OrderListReport, AdjustmentsReport, DiscountsReport, RefundsReport, RefundDetailReport, DrawerDetailReport, DrawerReceiptsReport, DrawerSalesSummaryReport, TipsPaidInReport, GetExport,
  ...timeSegmentQueries,
] as const;
export const reportCommands = [RequestExport, ...timeSegmentCommands] as const;
